import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import BuildingDetails from './BuildingDetails';
import { useTranslation } from 'react-i18next';

interface Building {
  id: number;
  name: string;
  address: string;
  description?: string;
  manager_name?: string;
  manager_phone?: string;
  manager_email?: string;
  total_floors: number;
  flats_per_floor: number;
  total_flats: number;
  status: 'active' | 'inactive' | 'maintenance';
  amenities?: string[];
  rules?: string[];
  maintenance_fee: number;
  image_url?: string;
  created_at: string;
  updated_at: string;
  statistics: {
    total_flats: number;
    occupied_flats: number;
    available_flats: number;
    occupancy_rate: number;
    total_floors: number;
    committee_members: number;
  };
}

interface BuildingFormData {
  name: string;
  address: string;
  description: string;
  manager_name: string;
  manager_phone: string;
  manager_email: string;
  total_floors: number;
  flats_per_floor: number;
  status: 'active' | 'inactive' | 'maintenance';
  amenities: string[];
  rules: string[];
  maintenance_fee: number;
  image_url: string;
}

const BuildingManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'list' | 'create' | 'edit' | 'details' | 'statistics'>('list');
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const [editingBuilding, setEditingBuilding] = useState<Building | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'maintenance'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statistics, setStatistics] = useState<any>(null);

  const { t } = useTranslation();
  const [buildingForm, setBuildingForm] = useState<BuildingFormData>({
    name: '',
    address: '',
    description: '',
    manager_name: '',
    manager_phone: '',
    manager_email: '',
    total_floors: 13,
    flats_per_floor: 8,
    status: 'active',
    amenities: [],
    rules: [],
    maintenance_fee: 0,
    image_url: '',
  });

  const fetchBuildings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('page', currentPage.toString());
      params.append('per_page', '12');

      const response = await api.get(`/buildings?${params}`);
      
      if (response.data.success) {
        setBuildings(response.data.data.data || response.data.data);
        setTotalPages(response.data.data.last_page || 1);
      } else {
        setError(response.data.message || 'Failed to fetch buildings');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching buildings');
      console.error('Error fetching buildings:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, currentPage]);

  useEffect(() => {
    if (activeTab === 'list' || activeTab === 'statistics') {
      fetchBuildings();
    }
    if (activeTab === 'statistics') {
      fetchStatistics();
    }
  }, [activeTab, fetchBuildings]);

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/buildings-statistics');
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (err: any) {
      console.error('Error fetching statistics:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const response = editingBuilding
        ? await api.put(`/buildings/${editingBuilding.id}`, buildingForm)
        : await api.post('/buildings', buildingForm);

      if (response.data.success) {
        setActiveTab('list');
        resetForm();
        fetchBuildings();
      } else {
        setError(response.data.message || 'Failed to save building');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving building');
      console.error('Error saving building:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (building: Building) => {
    setEditingBuilding(building);
    setBuildingForm({
      name: building.name,
      address: building.address,
      description: building.description || '',
      manager_name: building.manager_name || '',
      manager_phone: building.manager_phone || '',
      manager_email: building.manager_email || '',
      total_floors: building.total_floors,
      flats_per_floor: building.flats_per_floor,
      status: building.status,
      amenities: building.amenities || [],
      rules: building.rules || [],
      maintenance_fee: building.maintenance_fee,
      image_url: building.image_url || '',
    });
    setActiveTab('edit');
  };


  const handleViewDetails = async (building: Building) => {
    try {
      setLoading(true);
      const response = await api.get(`/buildings/${building.id}`);
      
      if (response.data.success) {
        setSelectedBuilding(response.data.data);
        setActiveTab('details');
      } else {
        setError(response.data.message || 'Failed to fetch building details');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching building details');
      console.error('Error fetching building details:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setBuildingForm({
      name: '',
      address: '',
      description: '',
      manager_name: '',
      manager_phone: '',
      manager_email: '',
      total_floors: 13,
      flats_per_floor: 8,
      status: 'active',
      amenities: [],
      rules: [],
      maintenance_fee: 0,
      image_url: '',
    });
    setEditingBuilding(null);
  };

  const handleAmenityChange = (amenity: string, checked: boolean) => {
    if (checked) {
      setBuildingForm(prev => ({
        ...prev,
        amenities: [...prev.amenities, amenity]
      }));
    } else {
      setBuildingForm(prev => ({
        ...prev,
        amenities: prev.amenities.filter(a => a !== amenity)
      }));
    }
  };

  const handleRuleChange = (index: number, value: string) => {
    const newRules = [...buildingForm.rules];
    newRules[index] = value;
    setBuildingForm(prev => ({ ...prev, rules: newRules }));
  };

  const addRule = () => {
    setBuildingForm(prev => ({
      ...prev,
      rules: [...prev.rules, '']
    }));
  };

  const removeRule = (index: number) => {
    setBuildingForm(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index)
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'maintenance': return 'warning';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return '✅';
      case 'inactive': return '⏸️';
      case 'maintenance': return '🔧';
      default: return '❓';
    }
  };

  const getStatusLabel = (status: string) => {
    return t(`buildingManager.status.${status}` as any);
  };

  if (loading && buildings.length === 0) {
    return (
      <div className="building-manager-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h3>Loading Buildings...</h3>
          <p>Please wait while we fetch the building data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="building-manager">
      {/* Header */}
      <div className="building-header">
        <div className="header-content">
          <h1>{t('buildingManager.title')}</h1>
          <p>{t('buildingManager.subtitle')}</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setActiveTab('create');
            }}
          >
            + {t('buildingManager.addBuilding')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="building-tabs">
        <button
          className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          📋 {t('buildingManager.listTab')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('statistics')}
        >
          📊 {t('buildingManager.statsTab')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => {
            resetForm();
            setActiveTab('create');
          }}
        >
          ➕ {t('buildingManager.createTab')}
        </button>
        {editingBuilding && (
          <button
            className={`tab-btn ${activeTab === 'edit' ? 'active' : ''}`}
            onClick={() => setActiveTab('edit')}
          >
            ✏️ {t('buildingManager.editTab')}
          </button>
        )}
        {selectedBuilding && (
          <button
            className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            👁️ {t('buildingManager.detailsTab')}
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          <div className="error-icon">⚠️</div>
          <div className="error-text">{error}</div>
          <button className="error-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Tab Content */}
      <div className="building-content">
        {activeTab === 'list' && (
          <div className="buildings-list">
            {/* Filters */}
            <div className="building-filters">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('buildingManager.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="search-btn">🔍</button>
              </div>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('all')}
                >
                  {t('buildingManager.filter.all')}
                </button>
                <button
                  className={`filter-btn ${statusFilter === 'active' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('active')}
                >
                  {t('buildingManager.filter.active')}
                </button>
                <button
                  className={`filter-btn ${statusFilter === 'inactive' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('inactive')}
                >
                  {t('buildingManager.filter.inactive')}
                </button>
                <button
                  className={`filter-btn ${statusFilter === 'maintenance' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('maintenance')}
                >
                  {t('buildingManager.filter.maintenance')}
                </button>
              </div>
            </div>

            {/* Buildings Grid */}
            <div className="buildings-grid">
              {buildings.map((building) => (
                <div key={building.id} className="building-card">
                  <div className="building-card-header">
                    <div className="header-info">
                      <h3>{building.name}</h3>
                      <p className="building-address">{building.address}</p>
                    </div>
                    <span className={`status-pill status-${building.status}`}>
                      {getStatusIcon(building.status)} {getStatusLabel(building.status)}
                    </span>
                  </div>

                  <div className="building-card-content">
                    {building.description && (
                      <p className="building-description">{building.description}</p>
                    )}

                    <div className="building-metrics">
                      <div className="metric">
                        <span className="metric-label">{t('buildingManager.totalFlats')}</span>
                        <span className="metric-value">{building.statistics.total_flats}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">{t('buildingManager.occupied')}</span>
                        <span className="metric-value">{building.statistics.occupied_flats}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">{t('buildingManager.occupancy')}</span>
                        <span className="metric-value">{building.statistics.occupancy_rate}%</span>
                      </div>
                    </div>

                    <div className="building-meta">
                      <div className="meta-item">
                        <span className="meta-label">{t('buildingManager.floors')}</span>
                        <span className="meta-value">{building.total_floors}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">{t('buildingManager.manager')}</span>
                        <span className="meta-value">{building.manager_name || t('buildingManager.managerNotAssigned')}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">{t('buildingManager.maintenance')}</span>
                        <span className="meta-value">৳{building.maintenance_fee.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="building-card-footer">
                    <button
                      className="card-btn primary"
                      onClick={() => handleViewDetails(building)}
                    >
                      {t('buildingManager.viewDetails')}
                    </button>
                    <button
                      className="card-btn secondary"
                      onClick={() => handleEdit(building)}
                    >
                      {t('buildingManager.edit')}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </button>
                
                <div className="pagination-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'statistics' && statistics && (
          <div className="building-statistics">
            <h2>Building Statistics Overview</h2>
            
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">🏢</div>
                <div className="stat-content">
                  <h3>{statistics.total_buildings}</h3>
                  <p>Total Buildings</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div className="stat-content">
                  <h3>{statistics.active_buildings}</h3>
                  <p>Active Buildings</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">🏠</div>
                <div className="stat-content">
                  <h3>{statistics.total_flats}</h3>
                  <p>Total Flats</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-content">
                  <h3>{statistics.occupied_flats}</h3>
                  <p>Occupied Flats</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">📊</div>
                <div className="stat-content">
                  <h3>{statistics.occupancy_rate}%</h3>
                  <p>Occupancy Rate</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">🆓</div>
                <div className="stat-content">
                  <h3>{statistics.available_flats}</h3>
                  <p>Available Flats</p>
                </div>
              </div>
            </div>

            {/* Charts will be added here */}
            <div className="charts-section">
              <h3>Building Status Distribution</h3>
              <div className="chart-placeholder">
                <p>Charts will be implemented here</p>
              </div>
            </div>
          </div>
        )}

        {(activeTab === 'create' || activeTab === 'edit') && (
          <div className="building-form">
            <h2>{editingBuilding ? 'Edit Building' : 'Add New Building'}</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">Building Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={buildingForm.name}
                    onChange={(e) => setBuildingForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                    placeholder="e.g., Building-01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address">Address *</label>
                  <input
                    type="text"
                    id="address"
                    value={buildingForm.address}
                    onChange={(e) => setBuildingForm(prev => ({ ...prev, address: e.target.value }))}
                    required
                    placeholder="e.g., Shopnonagar, Building 1"
                  />
                </div>

                <div className="form-group full-width">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={buildingForm.description}
                    onChange={(e) => setBuildingForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Building description..."
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="total_floors">Total Floors *</label>
                  <input
                    type="number"
                    id="total_floors"
                    value={buildingForm.total_floors}
                    onChange={(e) => setBuildingForm(prev => ({ ...prev, total_floors: parseInt(e.target.value) }))}
                    required
                    min="1"
                    max="50"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="flats_per_floor">Flats per Floor *</label>
                  <input
                    type="number"
                    id="flats_per_floor"
                    value={buildingForm.flats_per_floor}
                    onChange={(e) => setBuildingForm(prev => ({ ...prev, flats_per_floor: parseInt(e.target.value) }))}
                    required
                    min="1"
                    max="20"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="status">Status *</label>
                  <select
                    id="status"
                    value={buildingForm.status}
                    onChange={(e) => setBuildingForm(prev => ({ ...prev, status: e.target.value as any }))}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="maintenance_fee">Maintenance Fee (৳) *</label>
                  <input
                    type="number"
                    id="maintenance_fee"
                    value={buildingForm.maintenance_fee}
                    onChange={(e) => setBuildingForm(prev => ({ ...prev, maintenance_fee: parseFloat(e.target.value) }))}
                    required
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="manager_name">Manager Name</label>
                  <input
                    type="text"
                    id="manager_name"
                    value={buildingForm.manager_name}
                    onChange={(e) => setBuildingForm(prev => ({ ...prev, manager_name: e.target.value }))}
                    placeholder="Manager's full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="manager_phone">Manager Phone</label>
                  <input
                    type="tel"
                    id="manager_phone"
                    value={buildingForm.manager_phone}
                    onChange={(e) => setBuildingForm(prev => ({ ...prev, manager_phone: e.target.value }))}
                    placeholder="+880XXXXXXXXXX"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="manager_email">Manager Email</label>
                  <input
                    type="email"
                    id="manager_email"
                    value={buildingForm.manager_email}
                    onChange={(e) => setBuildingForm(prev => ({ ...prev, manager_email: e.target.value }))}
                    placeholder="manager@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="image_url">Image URL</label>
                  <input
                    type="url"
                    id="image_url"
                    value={buildingForm.image_url}
                    onChange={(e) => setBuildingForm(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Amenities</h3>
                <div className="amenities-grid">
                  {['elevator', 'parking', 'security', 'gym', 'garden', 'playground', 'swimming_pool', 'club_house'].map(amenity => (
                    <label key={amenity} className="amenity-checkbox">
                      <input
                        type="checkbox"
                        checked={buildingForm.amenities.includes(amenity)}
                        onChange={(e) => handleAmenityChange(amenity, e.target.checked)}
                      />
                      <span className="amenity-label">{amenity.replace('_', ' ').toUpperCase()}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-section">
                <h3>Building Rules</h3>
                {buildingForm.rules.map((rule, index) => (
                  <div key={index} className="rule-input-group">
                    <input
                      type="text"
                      value={rule}
                      onChange={(e) => handleRuleChange(index, e.target.value)}
                      placeholder="Enter building rule..."
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => removeRule(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={addRule}
                >
                  + Add Rule
                </button>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    resetForm();
                    setActiveTab('list');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (editingBuilding ? 'Update Building' : 'Create Building')}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'details' && selectedBuilding && (
          <BuildingDetails 
            buildingId={selectedBuilding.id} 
            onBack={() => setActiveTab('list')} 
          />
        )}
      </div>
    </div>
  );
};

export default BuildingManager;
